import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ABHA_CONSENT_STATEMENT_IDS } from '../../services/api/abhaConsentIds';
import { useTheme } from '../../theme/ThemeContext';
import OtpInput from '../auth/OtpInput';
import MaskedAadhaarInput from '../common/MaskedAadhaarInput';
import { getStoredSession } from '../../services/api/sessionApi';
import ABHAVerificationFlow from './ABHAVerificationFlow';
import PrimaryButton from '../auth/PrimaryButton';
import ResendOtp from '../auth/ResendOtp';
import useOtpResend from '../../hooks/useOtpResend';
import { isValidAadhaar, AADHAAR_INVALID_MESSAGE } from '../../utils/aadhaar';
import {
  ABHA_ADDRESS_RULES_TEXT,
  ABHA_ADDRESS_EXISTS_MESSAGE,
  validateAbhaAddressLocal,
} from '../../utils/abhaAddressRules';
import {
  requestCreationOTP,
  verifyCreationOTP,
  requestCreationMobileOTP,
  verifyCreationMobileOTP,
  getAddressSuggestions,
  setABHAAddress,
  downloadABHACard,
  downloadVerifiedABHACard,
} from '../../services/api/abhaApi';
import { CREATE_FLOW, SKIP_ABHA, NO_MORE_RESENDS, ABHA_NUMBER_LABEL } from '../../constants/abdmCopy';

/**
 * ABHACreationFlow — 4-step ABHA number creation via Aadhaar OTP.
 *
 * Steps: consent → otp → address → card
 *
 * Accepts 12-digit Aadhaar number.
 * Declaration is expanded by default; sub-checkboxes shown outside for clarity.
 */

const STEPS = ['consent', 'otp', 'address', 'card'];
const STEP_LABELS = CREATE_FLOW.stepLabels;

// ── Step progress indicator ───────────────────────────────────────────

function StepBar({ step, isDark }) {
  const idx = STEPS.indexOf(step === 'mobile' ? 'otp' : step); // 'mobile' is a sub-step of OTP
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20 }}>
      {STEP_LABELS.map((label, i) => {
        const done   = i < idx;
        const active = i === idx;
        return (
          <React.Fragment key={label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0,
                border: `2px solid ${done || active ? (isDark ? '#0d9488' : '#0f766e') : (isDark ? '#475569' : '#cbd5e1')}`,
                background: done || active ? (isDark ? '#0d9488' : '#0f766e') : (isDark ? '#1e293b' : '#f8fafc'),
                color: done || active ? '#fff' : isDark ? '#64748b' : '#94a3b8',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                color: active
                  ? isDark ? '#2dd4bf' : '#0f766e'
                  : isDark ? '#64748b' : '#9ca3af',
              }}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                flex: 1, height: 1.5,
                background: i < idx
                  ? isDark ? '#0d9488' : '#99f6e4'
                  : isDark ? '#334155' : '#e2e8f0',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Error banner ──────────────────────────────────────────────────────

function ErrorBanner({ error, onSkip, isDark }) {
  if (!error) return null;
  return (
    <div style={{
      borderRadius: 8, padding: '10px 12px', marginBottom: 14,
      border: `1px solid ${isDark ? '#7f1d1d' : '#fca5a5'}`,
      background: isDark ? 'rgba(127,29,29,0.35)' : '#fef2f2',
    }}>
      <p style={{ fontSize: 13, color: isDark ? '#fca5a5' : '#b91c1c', margin: 0, marginBottom: error.allowSkip ? 8 : 0 }}>
        {error.userMessage}
      </p>
      {error.allowSkip && (
        <button onClick={onSkip} style={{
          fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 5,
          border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
          background: isDark ? '#1e293b' : '#fff',
          color: isDark ? '#94a3b8' : '#64748b', cursor: 'pointer',
        }}>
          {SKIP_ABHA}
        </button>
      )}
    </div>
  );
}

// ── Input + label helpers ─────────────────────────────────────────────

function inputStyle(isDark, hasError = false) {
  return {
    width: '100%', padding: '10px 12px', fontSize: 14,
    border: `1.5px solid ${hasError ? '#ef4444' : isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
    borderRadius: 8,
    background: isDark ? 'rgba(255,255,255,0.06)' : '#f9fafb',
    color: isDark ? '#f5f5f7' : '#111827',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };
}

function labelStyle(isDark) {
  return {
    display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600,
    color: isDark ? 'rgba(255,255,255,0.55)' : '#374151', letterSpacing: '0.2px',
  };
}

function helperText(isDark, type = 'hint') {
  return {
    fontSize: 11, marginTop: 4,
    color: type === 'error'
      ? '#ef4444'
      : type === 'success'
      ? (isDark ? '#34d399' : '#059669')
      : type === 'warn'
        ? (isDark ? '#fbbf24' : '#d97706')
        : (isDark ? '#64748b' : '#9ca3af'),
  };
}

// ── Aadhaar display formatter ─────────────────────────────────────────

function formatIdDisplay(raw) {
  const d = raw.replace(/\D/g, '').slice(0, 12);
  if (d.length <= 4)  return d;
  if (d.length <= 8)  return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 8)}-${d.slice(8)}`;
}

// ── Consent section groups ────────────────────────────────────────────

// Statements staff must tick (unticked by default) with the patient. Order = flat order below.
const REQUIRED_STATEMENT_COUNT = ABHA_CONSENT_STATEMENT_IDS.length;

const CONSENT_GROUPS = [
  {
    heading: 'For this enrollment',
    items: [
      { id: ABHA_CONSENT_STATEMENT_IDS[0], required: true, text: 'I am voluntarily sharing my Aadhaar Number / Virtual ID issued by the Unique Identification Authority of India ("UIDAI"), and my demographic information for the purpose of creating an Ayushman Bharat Health Account number ("ABHA number") and Ayushman Bharat Health Account address ("ABHA Address"). I authorize NHA to use my Aadhaar number / Virtual ID for performing Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose. I understand that UIDAI will share my e-KYC details, or response of "Yes" with NHA upon successful authentication.' },
      { text: 'I intend to create Ayushman Bharat Health Account Number ("ABHA number") and Ayushman Bharat Health Account address ("ABHA Address") using document other than Aadhaar. (Click here to proceed further)', muted: true },
    ],
  },
  {
    heading: 'About your health data',
    items: [
      { id: ABHA_CONSENT_STATEMENT_IDS[1], required: true, text: 'I consent to usage of my ABHA address and ABHA number for linking of my legacy (past) government health records and those which will be generated during this encounter.' },
      { id: ABHA_CONSENT_STATEMENT_IDS[2], required: true, text: 'I authorize the sharing of all my health records with healthcare provider(s) for the purpose of providing healthcare services to me during this encounter.' },
      { id: ABHA_CONSENT_STATEMENT_IDS[3], required: true, text: 'I consent to the anonymization and subsequent use of my government health records for public health purposes.' },
    ],
  },
];

// ── Main component ────────────────────────────────────────────────────

export default function ABHACreationFlow({ onComplete, onSkip, onSwitchToVerify, patientName = '', defaultMobile = '' }) {
  const { isDark } = useTheme();

  const _mobileFromParent = String(defaultMobile || '').replace(/\D/g, '').slice(0, 10);

  const [step, setStep]                         = useState('consent');
  const [aadhaarRaw, setAadhaarRaw]             = useState('');
  // Mobile is bound to the registration/booking mobile above — populated + locked (only changes
  // when that top-level number changes).
  const [mobileRaw, setMobileRaw]               = useState(_mobileFromParent);
  const [consentGiven, setConsentGiven]         = useState(false);
  const [statementTicks, setStatementTicks]     = useState(() => Array(REQUIRED_STATEMENT_COUNT).fill(true)); // R224: pre-ticked as in the NHA published form
  const [workerConsent, setWorkerConsent]       = useState(false);
  const [beneficiaryConsent, setBeneficiaryConsent] = useState(false);
  const [beneficiaryName, setBeneficiaryName]   = useState(patientName || '');
  const workerName = getStoredSession()?.displayName || '';
  useEffect(() => {
    if (patientName) setBeneficiaryName((prev) => prev || patientName);
  }, [patientName]);
  const [declarationExpanded, setDeclarationExpanded] = useState(true);
  const [txnId, setTxnId]                       = useState('');
  const [otp, setOtp]                           = useState('');
  const [profile, setProfile]                   = useState(null);
  const [suggestions, setSuggestions]           = useState([]);
  const [selectedAddress, setSelectedAddress]   = useState('');
  const [useCustom, setUseCustom]               = useState(false);
  const [customAddress, setCustomAddress]       = useState('');
  const [cardUrl, setCardUrl]                   = useState(null);
  const [cardError, setCardError]               = useState(false);
  const [inlineVerify, setInlineVerify]         = useState(false);
  // Existing ABHA -> "Add another ABHA address": the address step, behind a preferred-address tick.
  const [addingAddress, setAddingAddress]       = useState(false);
  const [preferredAgreed, setPreferredAgreed]   = useState(false);
  const [wantsNewAddress, setWantsNewAddress]   = useState(false);   // existing ABHA: "Create a new" chosen
  const [loading, setLoading]                   = useState(false);
  const [error, setError]                       = useState(null);
  const [maskedMobile, setMaskedMobile]         = useState(null);
  const [addressSuffix, setAddressSuffix]       = useState('');
  const { secondsLeft, canResend, resendsLeft, markSent, reset: resetResend } = useOtpResend();
  const [mobileOtp, setMobileOtp]               = useState('');
  const [mobileMasked, setMobileMasked]         = useState(null);
  const mobileResend = useOtpResend();
  const cardUrlRef = useRef(null);

  useEffect(() => () => {
    if (cardUrlRef.current) URL.revokeObjectURL(cardUrlRef.current);
  }, []);

  // Keep the (locked) mobile in sync if the top-level registration/booking number changes.
  useEffect(() => {
    if (_mobileFromParent) setMobileRaw(_mobileFromParent);
  }, [_mobileFromParent]);

  const idRaw = aadhaarRaw.replace(/\D/g, '').slice(0, 12);
  const idLen = idRaw.length;
  const isValidId = idLen === 12;

  const idDisplay = formatIdDisplay(idRaw);

  const idHint = (() => {
    if (idLen === 0) return null;
    if (!isValidId) return { text: 'Enter all 12 digits', type: 'warn' };
    if (!isValidAadhaar(idRaw)) return { text: AADHAAR_INVALID_MESSAGE, type: 'error' };
    return { text: '✓ Aadhaar number ready', type: 'success' };
  })();

  const handleIdChange = (e) => {
    setAadhaarRaw(e.target.value.replace(/\D/g, '').slice(0, 12));
    if (error) setError(null);
  };

  // The declaration actually acknowledged on this form -- sent to the server, which validates and records it.
  const consentDeclaration = useMemo(() => ({
    declarationAccepted: consentGiven && statementTicks.every(Boolean),
    acknowledgements: ABHA_CONSENT_STATEMENT_IDS.map((id, i) => ({ id, accepted: statementTicks[i] })),
    workerConsent,
    beneficiaryConsent,
    beneficiaryName: beneficiaryName.trim(),
  }), [consentGiven, statementTicks, workerConsent, beneficiaryConsent, beneficiaryName]);

  const allGated = consentGiven && statementTicks.every(Boolean) && workerConsent && beneficiaryConsent && beneficiaryName.trim().length > 0 && isValidId && isValidAadhaar(idRaw) && mobileRaw.length === 10;

  // Step 1 — send OTP
  const handleRequestOTP = useCallback(async () => {
    if (!allGated || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { txnId: tid, maskedMobile: mm } = await requestCreationOTP({ aadhaarNumber: idRaw, consentGiven, consent: consentDeclaration });
      setTxnId(tid);
      setMaskedMobile(mm);
      markSent();
      setStep('otp');
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [allGated, idRaw, loading, consentGiven, consentDeclaration, markSent]);

  const handleResendOTP = useCallback(async () => {
    if (!canResend || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { txnId: tid, maskedMobile: mm } = await requestCreationOTP({ aadhaarNumber: idRaw, consentGiven, consent: consentDeclaration, resend: true });
      setTxnId(tid);
      if (mm) setMaskedMobile(mm);
      markSent();
    } catch (err) {
      if (err.abhaCode === 'OTP_RESEND_TOO_SOON') markSent(err.retryAfter);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [canResend, idRaw, loading, consentGiven, consentDeclaration, markSent]);

  const goToAddressStep = async (tid) => {
    const { suggestions: sugg, txnId: updatedTxn, abhaAddressSuffix } = await getAddressSuggestions({ txnId: tid });
    setTxnId(updatedTxn || tid);
    setSuggestions(sugg);
    setAddressSuffix(abhaAddressSuffix || '');
    if (sugg.length > 0) setSelectedAddress(sugg[0]);
    setStep('address');
  };

  // Step 2 — verify OTP
  const handleVerifyOTP = useCallback(async () => {
    if (otp.length !== 6 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const prof = await verifyCreationOTP({ txnId, otp, mobile: mobileRaw });
      setProfile(prof);
      if (prof.isExisting) { setCardError(true); setStep('card'); return; }
      if (prof.mobileVerificationRequired) {
        // CRT_ABHA_109: communication mobile differs from the Aadhaar-linked one -> OTP-verify it first.
        const sent = await requestCreationMobileOTP({ txnId: prof.txnId, mobile: mobileRaw });
        setTxnId(sent.txnId || prof.txnId);
        setMobileMasked(sent.maskedMobile);
        mobileResend.markSent();
        setStep('mobile');
        return;
      }
      await goToAddressStep(prof.txnId);
    } catch (err) {
      if (err.code === 'CONFLICT' && onSwitchToVerify) { onSwitchToVerify(); return; }
      setError(err);
      setOtp('');
    } finally {
      setLoading(false);
    }
  }, [txnId, otp, loading, mobileRaw, onSwitchToVerify, mobileResend.markSent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2b — communication-mobile OTP (CRT_ABHA_109)
  const handleResendMobileOTP = useCallback(async () => {
    if (!mobileResend.canResend || loading) return;
    setLoading(true);
    setError(null);
    try {
      const sent = await requestCreationMobileOTP({ txnId, mobile: mobileRaw, resend: true });
      setTxnId(sent.txnId || txnId);
      if (sent.maskedMobile) setMobileMasked(sent.maskedMobile);
      mobileResend.markSent();
    } catch (err) {
      if (err.abhaCode === 'OTP_RESEND_TOO_SOON') mobileResend.markSent(err.retryAfter);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [txnId, mobileRaw, loading, mobileResend.canResend, mobileResend.markSent]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerifyMobileOTP = useCallback(async () => {
    if (mobileOtp.length !== 6 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { txnId: tid } = await verifyCreationMobileOTP({ txnId, otp: mobileOtp });
      await goToAddressStep(tid || txnId);
    } catch (err) {
      setError(err);
      setMobileOtp('');
    } finally {
      setLoading(false);
    }
  }, [txnId, mobileOtp, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Existing ABHA (owner 2026-11-01): open the address step for this enrolment. ABDM makes the new address
  // the patient's PREFERRED one, so that step shows a warning and needs a tick (see addAddressWarning).
  const handleAddAnotherAddress = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      setSelectedAddress('');
      setUseCustom(false);
      setCustomAddress('');
      setPreferredAgreed(false);
      await goToAddressStep(txnId);
      setAddingAddress(true);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [loading, txnId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 3 — set address
  const customAddressError = useCustom && customAddress.trim() ? validateAbhaAddressLocal(customAddress) : null;

  const handleSetAddress = useCallback(async () => {
    const address = useCustom ? customAddress.trim() : selectedAddress;
    if (!address || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await setABHAAddress({ txnId, abhaAddress: address });
      const updatedTxn = result.txnId || txnId;
      setTxnId(updatedTxn);
      // ABDM makes a saved address the PREFERRED one: put it first in the patient's address list.
      setProfile((prev) => ({
        ...prev,
        abhaAddress: result.abhaAddress,
        abhaAddresses: [result.abhaAddress, ...((prev?.abhaAddresses) || []).filter((a) => a !== result.abhaAddress)],
      }));
      setAddingAddress(false);
      setWantsNewAddress(false);
      setStep('card');
      downloadABHACard({ txnId: updatedTxn })
        .then(({ objectUrl }) => { cardUrlRef.current = objectUrl; setCardUrl(objectUrl); })
        .catch(() => setCardError(true));
    } catch (err) {
      if (err.abhaCode === 'ABHA_ADDRESS_EXISTS') err.userMessage = ABHA_ADDRESS_EXISTS_MESSAGE;
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [txnId, useCustom, customAddress, selectedAddress, loading]);

  const handleInlineVerified = useCallback(async (raw) => {
    setInlineVerify(false);
    if (!raw?.txnId) return;
    try {
      const { objectUrl } = await downloadVerifiedABHACard({ txnId: raw.txnId });
      cardUrlRef.current = objectUrl;
      setCardUrl(objectUrl);
      setCardError(false);
    } catch {
      setCardError(true);
    }
  }, []);

  const handleDone = () => {
    onComplete({
      consentWorkerName:      workerName,
      consentBeneficiaryName: beneficiaryName.trim(),
      registrationMobile:     mobileRaw,
      abhaNumber:  profile?.abhaNumber  ?? null,
      abhaAddress: profile?.abhaAddress ?? null,
      name:        profile?.name        ?? null,
      gender:      profile?.gender      ?? null,
      yearOfBirth: profile?.yearOfBirth ?? null,
      dob:         profile?.dob         ?? null,
      mobile:      profile?.mobile      ?? null,
      // Full profile — carried so the ABHAPROFILE record + demographics capture the address too.
      middleName:      profile?.middleName      ?? null,
      email:           profile?.email           ?? null,
      addressLine:     profile?.addressLine     ?? null,
      stateName:       profile?.stateName       ?? null,
      districtName:    profile?.districtName    ?? null,
      subdistrictName: profile?.subdistrictName ?? null,
      townName:        profile?.townName        ?? null,
      pincode:         profile?.pincode         ?? null,
      // The Aadhaar the user entered here — carried so the registration form's Aadhaar field
      // auto-fills (display-masked; never persisted with the ABHA profile).
      aadhaar:     idRaw || null,
    });
  };

  // ── Styles ─────────────────────────────────────────────────────────
  const panelStyle = {
    borderRadius: 12,
    border: `1px solid ${isDark ? 'rgba(20,184,166,0.3)' : '#99f6e4'}`,
    background: isDark ? '#1e293b' : '#f0fdfa',
    padding: '20px 20px 16px',
  };

  const secondaryBtn = {
    width: '100%', marginTop: 8, padding: '9px 12px',
    fontSize: 13, fontWeight: 600, borderRadius: 8,
    border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
    background: isDark ? '#0f172a' : '#fff',
    color: isDark ? '#94a3b8' : '#64748b', cursor: 'pointer',
  };

  const divider = (
    <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', margin: '16px 0' }} />
  );

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#2dd4bf' : '#0f766e' }}>
            {CREATE_FLOW.title}
          </h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: isDark ? '#64748b' : '#94a3b8' }}>
            {CREATE_FLOW.subtitle}
          </p>
        </div>
        {step !== 'card' && (
          <button onClick={onSkip} style={{
            fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6,
            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
            background: isDark ? '#1e293b' : '#fff',
            color: isDark ? '#64748b' : '#94a3b8', cursor: 'pointer', flexShrink: 0,
          }}>
            Skip
          </button>
        )}
      </div>

      <StepBar step={step} isDark={isDark} />
      <ErrorBanner error={error} onSkip={onSkip} isDark={isDark} />

      {/* ── Step: consent ── */}
      {step === 'consent' && (
        <div>
          {/* Aadhaar input */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle(isDark)}>Aadhaar Number</label>
            <MaskedAadhaarInput
              value={idDisplay}
              onChange={handleIdChange}
              placeholder="XXXX-XXXX-XXXX"
              maxLength={14}
              style={inputStyle(isDark)}
              isDark={isDark}
            />
            {idHint && (
              <p style={helperText(isDark, idHint.type)}>{idHint.text}</p>
            )}
            {idLen === 0 && (
              <p style={helperText(isDark, 'hint')}>{CREATE_FLOW.aadhaarHint}</p>
            )}
          </div>

          {/* Mobile input */}
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle(isDark)}>{CREATE_FLOW.mobileLabel}</label>
            <input
              type="text"
              inputMode="numeric"
              value={mobileRaw}
              onChange={(e) => { if (!_mobileFromParent) setMobileRaw(e.target.value.replace(/\D/g, '').slice(0, 10)); }}
              placeholder="10-digit mobile number"
              maxLength={10}
              readOnly={Boolean(_mobileFromParent)}
              disabled={Boolean(_mobileFromParent)}
              style={{ ...inputStyle(isDark), ...(_mobileFromParent ? { opacity: 0.75, cursor: 'not-allowed' } : {}) }}
              autoComplete="off"
            />
            <p style={helperText(isDark, mobileRaw.length === 10 ? 'success' : 'hint')}>
              {_mobileFromParent
                ? '✓ Using the mobile number from registration (change it above)'
                : (mobileRaw.length === 10
                  ? '✓ Mobile number ready'
                  : CREATE_FLOW.mobileHint)}
            </p>
          </div>

          {divider}

          {/* Trust signal */}
          <p style={{ margin: '0 0 14px', fontSize: 11, color: isDark ? '#475569' : '#9ca3af', textAlign: 'center' }}>
            🔒 Verified securely through UIDAI · National Health Authority
          </p>

          {/* Collapsed declaration */}
          <div style={{ marginBottom: 14 }}>
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              cursor: 'pointer', marginBottom: 8,
            }}>
              <input
                type="checkbox"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
                style={{ marginTop: 2, width: 15, height: 15, flexShrink: 0, accentColor: '#0d9488' }}
              />
              <span style={{ fontSize: 13, lineHeight: 1.5, color: isDark ? '#cbd5e1' : '#1f2937', fontWeight: 500 }}>
                I agree to create my ABHA using Aadhaar.
              </span>
            </label>

            <button
              type="button"
              onClick={() => setDeclarationExpanded((v) => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, padding: '2px 0',
                color: isDark ? '#2dd4bf' : '#0f766e',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {declarationExpanded ? '▲ Hide declaration' : '▼ Read full declaration'}
            </button>

            {declarationExpanded && (
              <div style={{
                marginTop: 12,
                borderRadius: 8,
                background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                padding: '14px 16px',
              }}>
                <p style={{
                  margin: '0 0 12px', fontSize: 12, fontWeight: 700,
                  color: isDark ? '#e2e8f0' : '#111827', textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  I hereby declare that:
                </p>

                {CONSENT_GROUPS.map((group, gi) => (
                  <div key={gi} style={{ marginBottom: gi < CONSENT_GROUPS.length - 1 ? 14 : 0 }}>
                    {/* Group heading */}
                    <p style={{
                      margin: '0 0 8px', fontSize: 11, fontWeight: 700,
                      color: isDark ? '#64748b' : '#6b7280',
                      textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>
                      {group.heading}
                    </p>
                    <div style={{
                      height: 1,
                      background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
                      marginBottom: 8,
                    }} />

                    {group.items.map(({ text, required }, ii) => {
                      const idx = CONSENT_GROUPS.slice(0, gi).reduce((n, g) => n + g.items.filter((i) => i.required).length, 0)
                        + group.items.slice(0, ii).filter((i) => i.required).length;
                      return (
                        <label key={ii} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8,
                          marginBottom: ii < group.items.length - 1 ? 8 : 0,
                          opacity: required ? 1 : 0.45,
                          cursor: required ? 'pointer' : 'default',
                        }}>
                          <input
                            type="checkbox"
                            data-testid={required ? `consent-statement-${idx}` : undefined}
                            checked={required ? statementTicks[idx] : false}
                            disabled={!required}
                            onChange={required ? (e) => {
                              const v = e.target.checked;
                              setStatementTicks((prev) => prev.map((t, i) => (i === idx ? v : t)));
                            } : undefined}
                            style={{ marginTop: 2, width: 14, height: 14, flexShrink: 0, accentColor: '#0d9488' }}
                          />
                          <span style={{
                            fontSize: 11, lineHeight: 1.65,
                            color: isDark ? '#94a3b8' : '#4b5563',
                          }}>
                            {text}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Healthcare staff confirmation — always visible */}
          <div style={{
            borderRadius: 8, padding: '12px 14px', marginBottom: 18,
            background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          }}>
            <p style={{
              margin: '0 0 8px', fontSize: 11, fontWeight: 700,
              color: isDark ? '#64748b' : '#6b7280',
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>
              Healthcare staff confirmation
            </p>
            <div style={{ height: 1, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)', marginBottom: 10 }} />

            {[
              {
                key: 'worker',
                checked: workerConsent,
                onChange: setWorkerConsent,
                text: (
                  <>
                    I (healthcare worker / staff){' '}
                    <span data-testid="consent-worker-name" style={{ fontWeight: 600 }}>{workerName}</span>
                    {' '}confirm that I have duly informed and explained the beneficiary of the contents of consent for the aforementioned purposes.
                  </>
                ),
              },
              {
                key: 'beneficiary',
                checked: beneficiaryConsent,
                onChange: setBeneficiaryConsent,
                text: (
                  <>
                    I{' '}
                    <input
                      type="text"
                      data-testid="consent-beneficiary-name"
                      aria-label="Beneficiary name"
                      value={beneficiaryName}
                      onChange={(e) => setBeneficiaryName(e.target.value)}
                      placeholder="Beneficiary name"
                      style={{
                        fontSize: 12, padding: '2px 6px', borderRadius: 4, width: 180,
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
                        background: isDark ? '#0f172a' : '#fff',
                        color: isDark ? '#e2e8f0' : '#111827',
                      }}
                    />
                    {' '}have been explained about the consent as stated above and hereby provide my consent for the aforementioned purposes.
                  </>
                ),
              },
            ].map(({ key, checked, onChange, text }) => (
              <label key={key} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                cursor: 'pointer', marginBottom: key === 'worker' ? 10 : 0,
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => onChange(e.target.checked)}
                  style={{ marginTop: 2, width: 14, height: 14, flexShrink: 0, accentColor: '#0d9488' }}
                />
                <span style={{ fontSize: 12, lineHeight: 1.6, color: isDark ? '#94a3b8' : '#374151' }}>
                  {text}
                </span>
              </label>
            ))}
          </div>

          <PrimaryButton
            onClick={handleRequestOTP}
            disabled={!allGated || loading}
          >
            {loading ? 'Sending OTP…' : 'Send OTP →'}
          </PrimaryButton>
        </div>
      )}

      {/* ── Step: otp ── */}
      {step === 'otp' && (
        <div>
          <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 14, marginTop: 0 }}>
            {maskedMobile
              ? `We just sent an OTP on the Mobile Number ${maskedMobile} linked with Aadhaar. Enter the OTP below to proceed with ABHA creation`
              : CREATE_FLOW.otpFallback}
          </p>
          <OtpInput value={otp} onChange={setOtp} disabled={loading} />
          <div style={{ marginTop: 10, marginBottom: 16 }}>
            {resendsLeft > 0 || secondsLeft > 0 ? (
              <ResendOtp onClick={handleResendOTP} disabled={!canResend || loading} seconds={secondsLeft} />
            ) : (
              <p style={helperText(isDark, 'hint')}>{NO_MORE_RESENDS}</p>
            )}
          </div>
          <PrimaryButton onClick={handleVerifyOTP} disabled={otp.length !== 6 || loading}>
            {loading ? 'Verifying…' : 'Verify OTP'}
          </PrimaryButton>
          <button onClick={() => { setStep('consent'); setOtp(''); setError(null); resetResend(); }} style={secondaryBtn}>
            ← Change Aadhaar number
          </button>
        </div>
      )}

      {/* ── Step: communication-mobile OTP ── */}
      {step === 'mobile' && (
        <div>
          <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 14, marginTop: 0 }}>
            {CREATE_FLOW.mobileStepTitle} We just sent an OTP on the {mobileMasked ? `Mobile Number ${mobileMasked}` : 'mobile number'}. Valid for 10 minutes.
          </p>
          <OtpInput value={mobileOtp} onChange={setMobileOtp} disabled={loading} />
          <div style={{ marginTop: 10, marginBottom: 16 }}>
            {mobileResend.resendsLeft > 0 || mobileResend.secondsLeft > 0 ? (
              <ResendOtp onClick={handleResendMobileOTP} disabled={!mobileResend.canResend || loading} seconds={mobileResend.secondsLeft} />
            ) : (
              <p style={helperText(isDark, 'hint')}>{NO_MORE_RESENDS}</p>
            )}
          </div>
          <PrimaryButton onClick={handleVerifyMobileOTP} disabled={mobileOtp.length !== 6 || loading}>
            {loading ? 'Verifying…' : 'Verify mobile OTP'}
          </PrimaryButton>
        </div>
      )}

      {/* ── Step: address ── */}
      {step === 'address' && (
        <div>
          {profile && (
            <div style={{
              borderRadius: 8, padding: '10px 12px', marginBottom: 14,
              border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
              background: isDark ? '#0f172a' : '#fff',
            }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                {profile.name}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: isDark ? '#64748b' : '#94a3b8' }}>
                {ABHA_NUMBER_LABEL} {profile.abhaNumber}
              </p>
            </div>
          )}

          <p style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#cbd5e1' : '#374151', marginBottom: 10, marginTop: 0 }}>
            {CREATE_FLOW.chooseAddress}
          </p>

          {suggestions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
              {suggestions.map((s) => {
                const selected = selectedAddress === s && !useCustom;
                return (
                  <label key={s} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                    border: `1.5px solid ${selected ? (isDark ? '#0d9488' : '#14b8a6') : (isDark ? '#334155' : '#e2e8f0')}`,
                    background: selected ? (isDark ? 'rgba(13,148,136,0.15)' : '#f0fdfa') : (isDark ? '#0f172a' : '#fff'),
                  }}>
                    <input
                      type="radio"
                      checked={selected}
                      onChange={() => { setSelectedAddress(s); setUseCustom(false); }}
                      style={{ accentColor: '#0d9488', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, fontFamily: 'monospace', color: isDark ? '#e2e8f0' : '#1e293b' }}>{s}</span>
                  </label>
                );
              })}
            </div>
          )}

          <label style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 10,
            border: `1.5px solid ${useCustom ? (isDark ? '#0d9488' : '#14b8a6') : (isDark ? '#334155' : '#e2e8f0')}`,
            background: useCustom ? (isDark ? 'rgba(13,148,136,0.15)' : '#f0fdfa') : (isDark ? '#0f172a' : '#fff'),
          }}>
            <input
              type="radio"
              checked={useCustom}
              onChange={() => setUseCustom(true)}
              style={{ accentColor: '#0d9488', flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b' }}>{CREATE_FLOW.otherAddress}</span>
          </label>

          {useCustom && (
            <div style={{ marginBottom: 14 }}>
              <input
                type="text"
                value={customAddress}
                onChange={(e) => setCustomAddress(e.target.value.replace(/[^a-zA-Z0-9._]/g, ''))}
                placeholder="yourname.city"
                style={inputStyle(isDark)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <p style={helperText(isDark, 'hint')}>
                {ABHA_ADDRESS_RULES_TEXT} Will become yourname.city{addressSuffix}
              </p>
              {customAddressError && (
                <p style={helperText(isDark, 'error')}>{customAddressError}</p>
              )}
            </div>
          )}

          {addingAddress && (
            <div role="note" style={{
              borderRadius: 8, padding: '10px 12px', marginBottom: 12,
              border: `1px solid ${isDark ? '#b45309' : '#f59e0b'}`,
              background: isDark ? '#422006' : '#fffbeb',
            }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, lineHeight: 1.5, color: isDark ? '#fde68a' : '#78350f' }}>
                {CREATE_FLOW.addAddressWarning}
              </p>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={preferredAgreed}
                  onChange={(e) => setPreferredAgreed(e.target.checked)}
                  style={{ marginTop: 2, width: 14, height: 14, flexShrink: 0, accentColor: '#b45309' }}
                />
                <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#fde68a' : '#78350f' }}>
                  {CREATE_FLOW.addAddressConfirm}
                </span>
              </label>
            </div>
          )}

          <PrimaryButton
            onClick={handleSetAddress}
            disabled={(useCustom ? !customAddress.trim() || Boolean(customAddressError) : !selectedAddress)
              || loading || (addingAddress && !preferredAgreed)}
          >
            {loading ? CREATE_FLOW.savingAddress : CREATE_FLOW.confirmAddress}
          </PrimaryButton>
          {addingAddress && (
            <button type="button" onClick={() => { setAddingAddress(false); setWantsNewAddress(false); setError(null); setStep('card'); }} style={secondaryBtn}>
              {CREATE_FLOW.addAddressBack}
            </button>
          )}
        </div>
      )}

      {/* ── Step: card ── */}
      {step === 'card' && (
        <div>
          <div style={{
            borderRadius: 10, padding: '14px 16px', marginBottom: 16, textAlign: 'center',
            border: `1px solid ${isDark ? '#065f46' : '#6ee7b7'}`,
            background: isDark ? 'rgba(6,78,59,0.4)' : '#ecfdf5',
          }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>✓</div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#6ee7b7' : '#065f46' }}>
              {profile?.isExisting ? CREATE_FLOW.foundExisting : CREATE_FLOW.created}
            </p>
            {profile?.abhaAddress && (
              <p style={{ margin: '4px 0 0', fontSize: 13, fontFamily: 'monospace', color: isDark ? '#94a3b8' : '#374151' }}>
                {profile.abhaAddress}
              </p>
            )}
            {profile?.abhaNumber && (
              <p style={{ margin: '2px 0 0', fontSize: 11, color: isDark ? '#64748b' : '#6b7280' }}>
                {ABHA_NUMBER_LABEL} {profile.abhaNumber}
              </p>
            )}
          </div>

          {cardUrl && (
            <div style={{ marginBottom: 14 }}>
              <img src={cardUrl} alt="ABHA Card" style={{
                width: '100%', borderRadius: 8,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              }} />
              <a href={cardUrl} download="ABHA-Card.png" style={{
                display: 'block', textAlign: 'center', marginTop: 8,
                padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                background: isDark ? '#0f172a' : '#fff',
                color: isDark ? '#94a3b8' : '#64748b',
                fontSize: 13, fontWeight: 600, textDecoration: 'none',
              }}>
                ↓ Download ABHA Card
              </a>
            </div>
          )}

          {!cardUrl && !cardError && (
            <p style={{ fontSize: 12, color: isDark ? '#475569' : '#9ca3af', marginBottom: 14, textAlign: 'center' }}>
              ABHA card is being prepared…
            </p>
          )}
          {!cardUrl && cardError && !profile?.isExisting && (
            <p style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#6b7280', marginBottom: 14, textAlign: 'center' }}>
              Card unavailable — download it later from the ABHA app.
            </p>
          )}
          {!cardUrl && cardError && profile?.isExisting && !inlineVerify && (
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#6b7280', margin: '0 0 8px', textAlign: 'center' }}>
                {CREATE_FLOW.existsConfirm}
              </p>
              <PrimaryButton onClick={() => setInlineVerify(true)}>
                Verify &amp; download card →
              </PrimaryButton>
            </div>
          )}
          {inlineVerify && (
            <div style={{ marginBottom: 14 }}>
              <ABHAVerificationFlow
                initialMethod="aadhaar"
                initialLoginInput={idRaw}
                title={CREATE_FLOW.verifyToDownload}
                subtitle={CREATE_FLOW.verifyToDownloadSub}
                onComplete={handleInlineVerified}
                onSkip={() => setInlineVerify(false)}
              />
            </div>
          )}

          {profile?.isExisting && !inlineVerify && (() => {
            // Existing ABHA (owner 2026-11-01): use one of the patient's ABHA addresses, or create a new one.
            const addresses = (profile.abhaAddresses && profile.abhaAddresses.length)
              ? profile.abhaAddresses : [profile.abhaAddress].filter(Boolean);
            const option = (checked) => ({
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
              border: `1.5px solid ${checked ? (isDark ? '#0d9488' : '#14b8a6') : (isDark ? '#334155' : '#cbd5e1')}`,
              background: checked ? (isDark ? '#134e4a' : '#f0fdfa') : (isDark ? '#0f172a' : '#fff'),
            });
            return (
              <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
                <legend style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#cbd5e1' : '#374151', marginBottom: 8 }}>
                  {CREATE_FLOW.addressChoiceTitle}
                </legend>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {addresses.map((a, i) => {
                    const checked = !wantsNewAddress && profile.abhaAddress === a;
                    return (
                      <label key={a} style={option(checked)}>
                        <input
                          type="radio" name="abha-address-choice" checked={checked}
                          onChange={() => { setWantsNewAddress(false); setProfile((prev) => ({ ...prev, abhaAddress: a })); }}
                          style={{ accentColor: '#0d9488', flexShrink: 0 }}
                        />
                        <span style={{ fontSize: 13, fontFamily: 'monospace', color: isDark ? '#e2e8f0' : '#1e293b' }}>{a}</span>
                        {i === 0 && (
                          <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>{CREATE_FLOW.preferredTag}</span>
                        )}
                      </label>
                    );
                  })}
                  <label style={option(wantsNewAddress)}>
                    <input
                      type="radio" name="abha-address-choice" checked={wantsNewAddress}
                      onChange={() => setWantsNewAddress(true)}
                      style={{ accentColor: '#0d9488', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, color: isDark ? '#cbd5e1' : '#374151' }}>{CREATE_FLOW.newAddressOption}</span>
                  </label>
                </div>
                {wantsNewAddress && (
                  <button type="button" onClick={handleAddAnotherAddress} disabled={loading} style={{ ...secondaryBtn, marginBottom: 0 }}>
                    {CREATE_FLOW.newAddressGo}
                  </button>
                )}
              </fieldset>
            );
          })()}

          <PrimaryButton onClick={handleDone} disabled={profile?.isExisting && wantsNewAddress}>
            {CREATE_FLOW.continueToRegistration}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}
